import type { Icon as PhosphorIcon } from '@phosphor-icons/react';
import {
  ArrowDown,
  ArrowElbowRightDown,
  ArrowElbowUpRight,
  ArrowLeft,
  ArrowRight,
  ArrowUp,
  ArrowUUpLeft,
  ArrowUUpRight,
  Barbell,
  Barcode,
  Basket,
  BeerStein,
  Bird,
  BoxArrowDown,
  BoxArrowUp,
  Briefcase,
  CalendarBlank,
  CalendarCheck,
  CaretDoubleRight,
  CarProfile,
  Chat,
  ChatText,
  Check,
  CheckCircle,
  CircleDashed,
  Clock,
  ClockCountdown,
  Dress,
  Factory,
  Gift,
  Golf,
  Hamburger,
  Hammer,
  HandArrowDown,
  HandArrowUp,
  HandWaving,
  Headset,
  Hourglass,
  HourglassHigh,
  House,
  HouseLine,
  Info,
  Lightning,
  Lockers,
  Package,
  PaperPlaneTilt,
  Question,
  ShirtFolded,
  ShoppingCart,
  Skull,
  SquareHalf,
  Storefront,
  Truck,
  Warehouse,
  Warning,
  Watch,
} from '@phosphor-icons/react/dist/ssr';

export const trackingIconMap = {
  'box-arrow-down': BoxArrowDown,
  'box-arrow-up': BoxArrowUp,
  chat: Chat,
  'chat-text': ChatText,
  clock: Clock,
  factory: Factory,
  'hand-arrow-down': HandArrowDown,
  'hand-arrow-up': HandArrowUp,
  hourglass: Hourglass,
  'hourglass-high': HourglassHigh,
  house: House,
  info: Info,
  lockers: Lockers,
  package: Package,
  'paper-plane-tilt': PaperPlaneTilt,
  question: Question,
  'shopping-cart': ShoppingCart,
  storefront: Storefront,
  truck: Truck,
  warehouse: Warehouse,
  warning: Warning,
  watch: Watch,
  check: Check,
  'check-circle': CheckCircle,
  'arrow-elbow-right-down': ArrowElbowRightDown,
  'arrow-elbow-up-right': ArrowElbowUpRight,
  'arrow-left': ArrowLeft,
  'arrow-right': ArrowRight,
  'arrow-up': ArrowUp,
  'arrow-down': ArrowDown,
  'arrow-u-up-left': ArrowUUpLeft,
  'arrow-u-up-right': ArrowUUpRight,
  barcode: Barcode,
  basket: Basket,
  bird: Bird,
  'calendar-blank': CalendarBlank,
  'calendar-check': CalendarCheck,
  'caret-double-right': CaretDoubleRight,
  'clock-countdown': ClockCountdown,
  gift: Gift,
  headset: Headset,
  lightning: Lightning,
  'square-half': SquareHalf,
  'circle-dashed': CircleDashed,
  'beer-stein': BeerStein,
  'car-profile': CarProfile,
  barbell: Barbell,
  golf: Golf,
  'shirt-folded': ShirtFolded,
  dress: Dress,
  briefcase: Briefcase,
  hammer: Hammer,
  skull: Skull,
  'house-line': HouseLine,
  hamburger: Hamburger,
  'hand-waving': HandWaving,
} as const;

export type TrackingIcon = keyof typeof trackingIconMap;

/**
 * Get a tracking icon component based on the icon name.
 *
 * To render the icon directly, use the {@link TrackingIcon} component.
 *
 * @param iconName The name of the icon to get.
 * @returns The icon component, or `undefined` if the icon name is not found.
 */
export function getTrackingIcon(
  iconName: TrackingIcon | ({} & string), // Hack to allow any string but still get intellisense
): PhosphorIcon | undefined {
  if (iconName in trackingIconMap) {
    return trackingIconMap[iconName as TrackingIcon];
  }

  return undefined;
}

interface TrackingIconProps extends React.ComponentProps<PhosphorIcon> {
  iconName: TrackingIcon | ({} & string); // Hack to allow any string but still get intellisense
  fallback?: PhosphorIcon;
}

/**
 * Render a tracking icon based on the icon name.
 *
 * If you want more control over the icon, you can use the {@link getTrackingIcon} function to get the icon component directly.
 */
export function TrackingIcon(props: TrackingIconProps) {
  const { iconName, fallback, ...rest } = props;

  const Icon = getTrackingIcon(iconName) || fallback;

  if (!Icon) {
    return null;
  }

  return <Icon {...rest} />;
}
